// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

<template>
  <v-card v-if="selectedObject" transparent
    :style="selectedObject && selectedObject.diamant ? getDiamondStyle(selectedObject.spectralColor) : { background: 'rgba(66, 66, 66, 0.6)', color: '#fff' }"
    :class="{
      'diamond-dark-text': selectedObject && selectedObject.diamant && getDiamondStyle(selectedObject.spectralColor).darkText
    }" style="max-width: 600px; width: 100%; max-height: 80vh; display: flex; flex-direction: column;">
    <div class="d-flex align-center" style="position: absolute; right: 8px; top: 8px;">

      <v-btn icon v-on:click.native="unselect()">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <!-- Title -->
    <v-card-title primary-title>
      <div style="width: 100%">
        <div class="d-flex align-center">
          <img :src="icon" height="48" width="48" align="left" style="margin-top: 3px; margin-right: 10px" />
          <div style="overflow: hidden; text-overflow: ellipsis; flex-grow: 1;">
            <div class="text-h5">{{ title }}</div>
            <div class="grey--text text-body-2">{{ title2 }}</div>
          </div>
        </div>
      </div>
    </v-card-title>
    <!-- Other names -->
    <v-card-text class="scrollable-content">
      <div v-if="otherNames.length > 1" class="mb-4 other-names">
        <span class="other-names-label">{{ $t('Also known as') }}</span>
        <span class="text-caption white--text" v-for="mname in otherNames1to7" :key="mname"
          style="margin-right: 15px; font-weight: 500;">{{ mname }}</span>
        <v-btn small icon class="grey--text" v-if="otherNames.length > 8"
          v-on:click.native="showMinorNames = !showMinorNames">
          <v-icon>mdi-dots-horizontal</v-icon>
        </v-btn>
        <span class="text-caption white--text" v-for="mname in otherNames8andMore" :key="mname"
          style="margin-right: 15px; font-weight: 500">{{ mname }}</span>
      </div>

      <v-row v-if="selectedObject && !selectedObject.diamant" style="width: 100%" no-gutters class="info-row">
        <v-col cols="12" class="field-description">
          Non visible à l’oeil nu dans le ciel nocturne
        </v-col>
      </v-row>

      <!-- Coordinates -->
      <v-row style="width: 100%" no-gutters class="info-row">
        <v-col cols="12" class="field-description">{{ $t('Az/Alt en temps réel') }}</v-col>
        <v-col cols="12" style="font-weight: 500" class="white--text">
          <span v-html="formatAz(coordinates.az) + '&nbsp;&nbsp;&nbsp;' + formatDec(coordinates.alt)"></span>
        </v-col>
      </v-row>
      <v-row style="width: 100%" no-gutters class="info-row">
        <v-col cols="12" class="field-description">{{ $t('Ra/Dec') }}</v-col>
        <v-col cols="12" style="font-weight: 500" class="white--text">
          <span v-html="formatRA(coordinates.ra) + '&nbsp;&nbsp;&nbsp;' + formatDec(coordinates.dec)"></span>
        </v-col>
      </v-row>

      <!-- Owner -->
      <div v-if="ownerItems.length > 0" class="mb-4 info-section">
        <v-row v-for="item in ownerItems" :key="item.key || item.id || item.name || i" style="width: 100%" no-gutters
          class="info-row">
          <v-col cols="12" class="field-description">{{ item.key }}</v-col>
          <v-col cols="12" style="font-weight: 500" class="white--text"><span v-html="item.value"></span></v-col>
        </v-row>
      </div>

      <!-- Diamond -->
      <div v-if="diamondItems.length > 0" class="mb-4 info-section">
        <div class="text-h6 mb-2">{{ $t('Diamant stellaire') }}</div>
        <!-- Image du diamant -->
        <div v-if="selectedObject && selectedObject.diamant" class="image-container diamond-background">
          <a :href="'https://cutwise.com/diamond/1294100?sp=45'" target="_blank" rel="noopener" class="diamond-link">
            <video autoplay loop muted playsinline class="object-video">
              <source src="https://cdn.stellardiamonds.org/uploads/stellar/video/diamant.mp4" type="video/mp4">
            </video>
          </a>
        </div>
        <!-- Grille 2x2 pour les caractéristiques principales -->
        <v-row v-if="selectedObject && selectedObject.diamant" class="diamond-grid mb-4">
          <!-- Type de diamant stellaire sur toute la largeur -->
          <v-col cols="12" v-if="selectedObject.diamant.type">
            <div class="diamond-grid-item">
              <div class="grid-label">{{ $t('Diamant stellaire') }}</div>
              <div class="grid-value">{{ selectedObject.diamant.type }}</div>
            </div>
          </v-col>
          <!-- Autres caractéristiques sur 2x2 -->
          <v-col cols="6">
            <div class="diamond-grid-item">
              <div class="grid-label">{{ $t('Taille') }}</div>
              <div class="grid-value">{{ selectedObject.diamant.cut }}</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="diamond-grid-item">
              <div class="grid-label">{{ $t('Carats') }}</div>
              <div class="grid-value">{{ selectedObject.diamant.carats }}</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="diamond-grid-item">
              <div class="grid-label">{{ $t('Couleur') }}</div>
              <div class="grid-value">{{ selectedObject.diamant.color }}</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="diamond-grid-item">
              <div class="grid-label">{{ $t('Clarté') }}</div>
              <div class="grid-value">{{ selectedObject.diamant.clarity }}</div>
            </div>
          </v-col>
        </v-row>
        <!-- Autres caractéristiques -->
        <v-row v-for="item in filteredDiamondItems" :key="item.key || item.id || item.name || i" style="width: 100%"
          no-gutters class="info-row">
          <v-col cols="12" class="field-description">{{ item.key }}</v-col>
          <v-col cols="12" style="font-weight: 500" class="white--text"><span v-html="item.value"></span></v-col>
        </v-row>
      </div>

      <!-- Star -->
      <div v-if="starItems.length > 0" class="mb-4 info-section">
        <div class="text-h6 mb-2">{{ $t('Caractéristiques de l\'étoile') }}</div>
        <!-- Caractéristiques principales -->
        <v-row v-for="item in mainStarItems" :key="item.key || item.id || item.name || i" style="width: 100%" no-gutters
          class="info-row">
          <v-col cols="12" class="field-description">{{ item.key }}</v-col>
          <v-col cols="12" style="font-weight: 500" class="white--text"><span v-html="item.value"></span></v-col>
        </v-row>

        <!-- Caractéristiques détaillées en accordéon -->
        <v-expansion-panels class="mt-4 expansion-panels">
          <v-expansion-panel>
            <v-expansion-panel-header class="panel-header">
              {{ $t('Caractéristiques détaillées') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content style="padding: 15px;">
              <v-row v-for="item in detailedStarItems" :key="item.key || item.id || item.name || i" style="width: 100%"
                no-gutters class="info-row">
                <v-col cols="12" class="field-description">{{ item.key }}</v-col>
                <v-col cols="12" style="font-weight: 500" class="white--text"><span v-html="item.value"></span>
                  <span v-if="item.unit"> {{ item.unit }}</span>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

       <!-- Music -->
       <div v-if="audioExists && selectedObject && selectedObject.diamant" class="mb-4 info-section">
        <!-- Titre et bouton de lecture de la musique de l'étoile -->
        <div v-if="audioExists" class="text-body-1 mb-2">
          Jouer la musique de cette étoile
          <!-- Bouton lecture/pause -->
          <v-btn v-if="audioExists" icon small class="mr-2" @click="playStarSound">
            <v-icon>{{ isPlaying ? 'mdi-pause-circle' : 'mdi-play-circle' }}</v-icon>
          </v-btn>
        </div>
        <div v-if="audioExists" ref="wavesurfer"
          style="width: 100%; height: 0; overflow: hidden; padding: 0; margin: 0; position: absolute; left: -9999px;">
        </div>
        <!-- Panneau d'expansion pour la forme d'onde audio -->
        <v-expansion-panels v-if="audioExists && selectedObject && selectedObject.diamant"
          class="mt-2 mb-2 expansion-panels" v-model="musicPanelOpen" :lazy="false">
          <v-expansion-panel>
            <!-- En-tête du panneau : titre de la forme d'onde -->
            <v-expansion-panel-header class="panel-header">
              {{ $t('Forme d\'onde') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content style="padding: 0;">
              <!-- Conteneur pour la forme d'onde -->
              <div ref="wavesurferSpectro" class="waveform-container"
                   :style="musicPanelOpen === 0 ? 'width: 100%; height: 150px;' : 'width: 100%; height: 120px; position: absolute; left: -9999px;'">
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

      </div>
      <!-- CTA -->
      <div v-if="selectedObject.availableForSale" class="d-flex flex-column justify-center align-center mb-6"
        style="width: 100%">
        <v-btn v-if="selectedObject.availableForSale == 'true'" color="primary" class="diamond-cta mx-auto mt-2"
          elevation="2" x-large href="https://www.deflorimont.com/contact?subject=check-availability" target="_blank"
          rel="noopener" block>
          <v-icon left class="mr-4">mdi-diamond-stone</v-icon>
          <div class="d-flex flex-column align-center">
            <span>{{ $t('Vérifier la disponibilité') }}</span>
            <span class="text-caption">{{ $t('demander une estimation') }}</span>
          </div>
        </v-btn>
      </div>

      
    </v-card-text>
    <v-card-actions style="margin-top: -25px">
      <v-spacer />
      <component v-for="item in pluginsSelectedInfoExtraGuiComponents" :is="item"
        :key="item.key || item.id || item.name || i"></component>
    </v-card-actions>
    <v-dialog v-model="showShareLinkDialog" width="500px" absolute>
      <v-card style="height: 180px" class="secondary white--text">
        <v-card-title primary-title>
          <div>
            <h3 class="text-h5 mb-0">Share link</h3>
          </div>
        </v-card-title>
        <v-card-text style="width:100%;">
          <v-row style="width: 100%">
            <v-text-field id="link_inputid" v-model="shareLink" label="Link" solo readonly></v-text-field>
            <v-btn @click.native.stop="copyLink">Copy</v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <div v-if="$store.state.showSelectedInfoButtons" style="position: absolute; right: 8px; bottom: -50px;">
      <v-btn v-if="!showPointToButton" fab small color="transparent"
        @click.native="showShareLinkDialog = !showShareLinkDialog">
        <v-icon :style="'color: #fff !important;'">
          mdi-link
        </v-icon>
      </v-btn>
      <v-btn v-if="showPointToButton" fab small color="transparent" v-on:click.native="lockToSelection()">
        <img src="@/assets/images/svg/ui/point_to.svg" height="40px" style="min-height: 40px"></img>
      </v-btn>
      <v-btn v-if="!showPointToButton" fab small color="transparent" @mousedown="zoomOutButtonClicked()">
        <img :class="{ bt_disabled: !zoomOutButtonEnabled }" src="@/assets/images/svg/ui/remove_circle_outline.svg"
          height="40px" style="min-height: 40px"></img>
      </v-btn>
      <v-btn v-if="!showPointToButton" fab small color="transparent" @mousedown="zoomInButtonClicked()">
        <img :class="{ bt_disabled: !zoomInButtonEnabled }" src="@/assets/images/svg/ui/add_circle_outline.svg"
          height="40px" style="min-height: 40px"></img>
      </v-btn>
    </div>
    <v-snackbar bottom left :timeout="2000" v-model="copied" color="secondary">
      Link copied
    </v-snackbar>
  </v-card>
</template>

<script>

import Moment from 'moment'
import swh from '@/assets/sw_helpers.js'
import WaveSurfer from 'wavesurfer.js'
import SpectrogramPlugin from 'wavesurfer.js/dist/plugins/spectrogram.esm.js'

export default {
  data: function () {
    return {
      showMinorNames: false,
      wikipediaData: undefined,
      shareLink: undefined,
      showShareLinkDialog: false,
      copied: false,
      items: [],
      timer: null,
      isPlaying: false,
      audioExists: false,
      coordinates: {
        ra: null,
        dec: null,
        az: null,
        alt: null,
        visibility: ''
      },
      wavesurfer: null,
      musicPanelOpen: 0
    }
  },
  computed: {
    selectedObject: function () {
      return this.$store.state.selectedObject
    },
    stelSelectionId: function () {
      return this.$store.state.stel && this.$store.state.stel.selection ? this.$store.state.stel.selection : undefined
    },
    title: function () {
      if (this.selectedObject.title) {
        return decodeURIComponent(this.selectedObject.title.title1)
      }
      return this.selectedObject ? this.otherNames[0] : 'Selection'
    },
    otherNames: function () {
      return this.selectedObject ? swh.namesForSkySource(this.selectedObject, 26) : undefined
    },
    otherNames1to7: function () {
      return this.otherNames.slice(1, 8)
    },
    otherNames8andMore: function () {
      return this.showMinorNames ? this.otherNames.slice(8) : []
    },
    wikipediaSummary: function () {
      if (!this.wikipediaData) return ''
      const page = this.wikipediaData.query.pages[Object.keys(this.wikipediaData.query.pages)[0]]
      if (!page || !page.extract) return ''
      const wl = '<b><a style="color: #62d1df;" target="_blank" rel="noopener" href="' + this.wikipediaLink + '">wikipedia</a></b></i>'
      return page.extract.replace(/<p>/g, '').replace(/<\/p>/g, '') + '<span class="grey--text text-caption" style="margin-left:auto; margin-right:0;"><i>&nbsp; ' + this.$t('more on {0}', [wl]) + '</span>'
    },
    wikipediaLink: function () {
      const page = this.wikipediaData.query.pages[Object.keys(this.wikipediaData.query.pages)[0]]
      if (!page || !page.extract) return ''
      return 'https://en.wikipedia.org/wiki/' + page.title
    },
    title2: function () {
      if (this.selectedObject.title) {
        return this.selectedObject.title.title2
      }
      return this.selectedObject ? this.selectedObject.types : '[*]'
    },
    type: function () {
      if (!this.selectedObject) return this.$t('Unknown')
      let morpho = ''
      if (this.selectedObject.model_data && this.selectedObject.model_data.morpho) {
        morpho = swh.nameForGalaxyMorpho(this.selectedObject.model_data.morpho)
        if (morpho) {
          morpho = morpho + ' '
        }
      }
      return morpho + swh.nameForSkySourceType(this.selectedObject.types[0])
    },
    icon: function () {
      return swh.iconForSkySource(this.selectedObject)
    },
    showPointToButton: function () {
      if (!this.$store.state.stel.lock) return true
      if (this.$store.state.stel.lock !== this.$store.state.stel.selection) return true
      return false
    },
    zoomInButtonEnabled: function () {
      if (!this.$store.state.stel.lock || !this.selectedObject) return false
      return true
    },
    zoomOutButtonEnabled: function () {
      if (!this.$store.state.stel.lock || !this.selectedObject) return false
      return true
    },
    extraButtons: function () {
      return swh.selectedObjectExtraButtons
    },
    pluginsSelectedInfoExtraGuiComponents: function () {
      let res = []
      for (const i in this.$stellariumWebPlugins()) {
        const plugin = this.$stellariumWebPlugins()[i]
        if (plugin.selectedInfoExtraGuiComponents) {
          res = res.concat(plugin.selectedInfoExtraGuiComponents)
        }
      }
      return res
    },
    ownerItems() {
      if (!this.selectedObject) return []
      const items = []
      if (this.selectedObject.givenName) {
        items.push({
          key: this.$t('Nom de l\'étoile'),
          value: this.selectedObject.givenName.toString()
        })
      }
      if (this.selectedObject.ownerPseudo) {
        items.push({
          key: this.$t("Propriétaire de l'étoile"),
          value: this.selectedObject.ownerPseudo.toString()
        })
      }
      if (this.selectedObject.givenBaptismDate) {
        items.push({
          key: this.$t('Date de baptême'),
          value: this.selectedObject.givenBaptismDate.toString()
        })
      }
      if (this.selectedObject.givenPublicMessage) {
        items.push({
          key: this.$t('Message personnel'),
          value: this.selectedObject.givenPublicMessage.toString()
        })
      }
      return items
    },
    diamondItems() {
      if (!this.selectedObject || !this.selectedObject.diamant) return []
      const items = []
      const diamant = this.selectedObject.diamant

      const diamondFields = [
        { key: 'Identifiant unique gravé dans le diamant', field: 'diamondUID' },
        { key: 'Diamant stellaire', field: 'type' },
        { key: 'Taille', field: 'cut' },
        { key: 'Carats', field: 'carats' },
        { key: 'Couleur', field: 'color' },
        { key: 'Clarté', field: 'clarity' },
        { key: 'NFT', field: 'nft' },
      ]

      diamondFields.forEach(field => {
        if (diamant[field.field]) {
          let value = diamant[field.field].toString();
          items.push({
            key: this.$t(field.key),
            value
          })
        }
      })
      return items
    },
    starItems() {
      if (!this.selectedObject.catalogueName) return []
      const items = []

      // Ajouter les champs statiques avec les valeurs
      const starFields = [
        { key: 'Nom officiel', field: 'catalogueName', value: this.selectedObject.catalogueName },
        { key: 'Visibility', field: 'visibility', value: this.coordinates.visibility },
        { key: 'Bayer Flamsteed', field: 'bayerFlamsteed', value: this.selectedObject.bayerFlamsteed },
        { key: 'Constellation', field: 'constellation', value: this.selectedObject.constellation },
        { key: 'Distance (parsecs)', field: 'distanceParsecs', value: this.selectedObject.distanceParsecs },
        { key: 'Distance (années-lumière)', field: 'distanceLightYears', value: this.selectedObject.distanceLightYears },
        { key: 'Magnitude visuelle (luminosité)', field: 'vmag', value: this.selectedObject.vmag },
        { key: 'Magnitude absolue (luminosité à une distance fixe)', field: 'magAbs', value: this.selectedObject.magAbs },
        { key: 'Indice de luminosité', field: 'magPercent', value: this.selectedObject.magPercent },
        { key: 'Couleur dominante', field: 'spectralColor', value: this.selectedObject.spectralColor }
      ]

      starFields.forEach(field => {
        items.push({
          field: field.field,
          key: this.$t(field.key),
          value: field.format
            ? field.format(field.value)
            : field.value.toString()
        })
      })

      return items
    },
    mainStarItems() {
      if (!this.starItems.length) return []
      // Retourne les items jusqu'à Bayer Flamsteed
      return this.starItems.filter(item => {
        const mainFields = ['catalogueName', 'visibility', 'bayerFlamsteed']
        return mainFields.includes(item.field)
      })
    },
    detailedStarItems() {
      if (!this.starItems.length) return []
      // Retourne les items à partir de Bayer Flamsteed
      return this.starItems.filter(item => {
        const mainFields = ['catalogueName', 'visibility', 'bayerFlamsteed']
        return !mainFields.includes(item.field)
      })
    },
    filteredDiamondItems() {
      if (!this.selectedObject || !this.selectedObject.diamant) return []
      const items = []
      const diamant = this.selectedObject.diamant

      // Liste des champs à exclure de la liste principale
      const excludedFields = ['cut', 'carats', 'color', 'clarity', 'type']

      const diamondFields = [
        { key: 'Identifiant unique gravé dans le diamant', field: 'diamondUID' },
        { key: 'Certificat IGI', field: 'iGICertificateNumber' },
        { key: 'Analyse gemmologique détaillée', field: 'urlCutwise' },
        { key: 'NFT', field: 'nft' }
      ]

      diamondFields.forEach(field => {
        if (diamant[field.field] && !excludedFields.includes(field.field)) {
          let value = diamant[field.field].toString();
          // Si c'est le champ urlCutwise, on l'affiche comme un lien cliquable
          if (field.field === 'urlCutwise' && value.includes('https')) {
            value = `<a href='${value}' target='_blank' rel='noopener' style='color:#62d1df;text-decoration:underline;'>${value}</a>`;
          }
          if (field.field === 'iGICertificateNumber' && value.startsWith('LG')) {
            value = `<a href='https://www.igi.org/Verify-Your-Report/?r=${value}' target='_blank' rel='noopener' style='color:#62d1df;text-decoration:underline;'>${value}</a>`;
          }
          items.push({
            key: this.$t(field.key),
            value
          })
        }
      })
      return items
    }
  },
  watch: {
    selectedObject: {
      handler(newVal) {
        // Logger les données de l'objet sélectionné
        // if (newVal && this.$stel && this.$stel.core.selection) {
        //   this.logSelectedObjectData(this.$stel.core.selection)
        // }
        
        this.showMinorNames = false
        this.wikipediaData = undefined
        this.audioExists = false

        // Vérifier et démarrer le nouvel audio si une étoile avec diamant est sélectionnée
        if (newVal && newVal.diamant && newVal.hr) {
          const audioBaseUrl = process.env.VUE_APP_DOMAIN_NAME.includes('localhost')
            ? '/stellar-sound'
            : 'https://cdn.stellardiamonds.org/uploads/stellar/sound'
          const audioPath = `${audioBaseUrl}/${this.selectedObject.hr}.mp3`
          // Vérifie réellement si le fichier existe
          fetch(audioPath, { method: 'HEAD' })
            .then(response => {
              this.audioExists = response.ok
              if (this.audioExists) {
                this.$nextTick(() => {
                  this.initWaveSurfer();
                });
              }
            })
            .catch(error => {
              console.error('Fichier audio non trouvé', audioPath)
              this.audioExists = false
            })
        }

        if (!newVal) {
          if (this.timer) clearInterval(this.timer)
          this.timer = undefined
          return
        }
        var that = this
        // Première mise à jour des coordonnées
        const obj = this.$stel.core.selection
        if (obj) {
          // Mise à jour Ra/Dec
          const posCIRS = this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'JNOW', obj.getInfo('radec'))
          const radecCIRS = this.$stel.c2s(posCIRS)
          this.$set(this.coordinates, 'ra', this.$stel.anp(radecCIRS[0]))
          this.$set(this.coordinates, 'dec', this.$stel.anpm(radecCIRS[1]))

          // Mise à jour Az/Alt
          const azalt = this.$stel.c2s(this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'OBSERVED', obj.getInfo('radec')))
          this.$set(this.coordinates, 'az', this.$stel.anp(azalt[0]))
          this.coordinates.alt = this.$stel.anpm(azalt[1])

          // Mise à jour visibilité
          const vis = obj.computeVisibility()
          let visStr = ''
          if (vis.length === 0) {
            visStr = this.$t('Not visible tonight')
          } else if (vis[0].rise === null) {
            visStr = this.$t('Always visible tonight')
          } else {
            visStr = this.$t('Rise: {0}&nbsp;&nbsp;&nbsp; Set: {1}', [
              this.formatTime(vis[0].rise),
              this.formatTime(vis[0].set)
            ])
          }
          this.$set(this.coordinates, 'visibility', visStr)
        }

        // Mise en place du timer pour les mises à jour
        if (that.timer) clearInterval(that.timer)
        that.timer = setInterval(() => {
          const obj = this.$stel.core.selection
          if (obj) {
            // Mise à jour Ra/Dec
            const posCIRS = this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'JNOW', obj.getInfo('radec'))
            const radecCIRS = this.$stel.c2s(posCIRS)
            this.$set(this.coordinates, 'ra', this.$stel.anp(radecCIRS[0]))
            this.$set(this.coordinates, 'dec', this.$stel.anpm(radecCIRS[1]))

            // Mise à jour Az/Alt
            const azalt = this.$stel.c2s(this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'OBSERVED', obj.getInfo('radec')))
            this.$set(this.coordinates, 'az', this.$stel.anp(azalt[0]))
            this.coordinates.alt = this.$stel.anpm(azalt[1])

            // Mise à jour visibilité
            const vis = obj.computeVisibility()
            let visStr = ''
            if (vis.length === 0) {
              visStr = this.$t('Not visible tonight')
            } else if (vis[0].rise === null) {
              visStr = this.$t('Always visible tonight')
            } else {
              visStr = this.$t('Rise: {0}&nbsp;&nbsp;&nbsp; Set: {1}', [
                this.formatTime(vis[0].rise),
                this.formatTime(vis[0].set)
              ])
            }
            this.$set(this.coordinates, 'visibility', visStr)
          }
        }, 1000)

        swh.getSkySourceSummaryFromWikipedia(newVal).then(data => {
          that.wikipediaData = data
        }, reason => {
        })

        // Détruire l'ancien wavesurfer si besoin
        if (this.wavesurfer) {
          this.wavesurfer.destroy()
          this.wavesurfer = null
        }
        // Si le panneau est ouvert, réinitialiser WaveSurfer pour la nouvelle étoile
        if (this.musicPanelOpen === 0) {
          this.audioExists = false;
          this.$nextTick(() => {
            this.initWaveSurfer();
          });
        }
      },
      immediate: true
    },
    stelSelectionId: function (s) {
      if (!this.$stel.core.selection) {
        this.$store.commit('setSelectedObject', 0)
        return
      }
      swh.sweObj2SkySource(this.$stel.core.selection).then(res => {
        this.$store.commit('setSelectedObject', res)
      }, err => {
        console.log("Couldn't find info for object " + s + ':' + err)
        this.$store.commit('setSelectedObject', 0)
      })
    },
    showShareLinkDialog: function (b) {
      this.shareLink = swh.getShareLink(this)
    },
    musicPanelOpen(val) {
      const isOpen = val === 0;
      console.log('[musicPanelOpen watcher] isOpen:', isOpen, 'audioExists:', this.audioExists, 'wavesurfer:', this.wavesurfer);
      if (isOpen && this.audioExists && !this.isPlaying) {
        this.$nextTick(() => {
          console.log('[musicPanelOpen watcher] Appel de initWaveSurfer');
          this.initWaveSurfer();
        });
      }
    }
  },
  methods: {
    logSelectedObjectData: function (obj) {
      console.log('=== OBJET SÉLECTIONNÉ ===')
      console.log('Objet:', obj)
      
      try {
        // Informations de base
        const designations = obj.designations() || []
        console.log('Désignations:', designations)
        
        // Chercher un nom propre (pas HIP, HD, HR, etc.)
        let nomPropre = ''
        for (const designation of designations) {
          if (!designation.startsWith('HIP ') && 
              !designation.startsWith('HD ') && 
              !designation.startsWith('HR ') &&
              !designation.startsWith('* ') &&
              designation.length > 0) {
            nomPropre = designation
            break
          }
        }
        console.log('Nom propre trouvé:', nomPropre || 'Aucun')
        
        // Variables utilisées dans le projet (comme dans selected-object-info.vue)
        console.log('--- Variables du projet ---')
        if (this.selectedObject) {
          console.log('selectedObject.title:', this.selectedObject.title)
          console.log('selectedObject.givenName:', this.selectedObject.givenName)
          console.log('selectedObject.catalogueName:', this.selectedObject.catalogueName)
          console.log('selectedObject.bayerFlamsteed:', this.selectedObject.bayerFlamsteed)
          console.log('selectedObject.constellation:', this.selectedObject.constellation)
          console.log('selectedObject.types:', this.selectedObject.types)
          console.log('selectedObject.hr:', this.selectedObject.hr)
          console.log('selectedObject.vmag:', this.selectedObject.vmag)
          console.log('selectedObject.distanceParsecs:', this.selectedObject.distanceParsecs)
          console.log('selectedObject.distanceLightYears:', this.selectedObject.distanceLightYears)
          console.log('selectedObject.spectralColor:', this.selectedObject.spectralColor)
        }
        
        // Essayer d'obtenir le nom via les méthodes du projet
        console.log('--- Méthodes du projet ---')
        try {
          if (this.selectedObject && this.selectedObject.getTitle) {
            console.log('getTitle():', this.selectedObject.getTitle())
          }
          if (this.selectedObject && this.selectedObject.getGivenName) {
            console.log('getGivenName():', this.selectedObject.getGivenName())
          }
          if (this.selectedObject && this.selectedObject.getCatalogueName) {
            console.log('getCatalogueName():', this.selectedObject.getCatalogueName())
          }
          if (this.selectedObject && this.selectedObject.getBayerFlamsteed) {
            console.log('getBayerFlamsteed():', this.selectedObject.getBayerFlamsteed())
          }
          
          // Méthodes SWE helpers
          if (this.selectedObject && swh.namesForSkySource) {
            console.log('swh.namesForSkySource():', swh.namesForSkySource(this.selectedObject, 26))
          }
          if (this.selectedObject && swh.nameForSkySourceType) {
            console.log('swh.nameForSkySourceType():', swh.nameForSkySourceType(this.selectedObject.types[0]))
          }
        } catch (e) {
          console.log('Erreur méthodes:', e.message)
        }
        
        // Coordonnées
        const radec = obj.getInfo('radec')
        if (radec) {
          const [ra, dec] = this.$stel.c2s(radec)
          const raHours = ra * 12 / Math.PI
          const decDegrees = dec * 180 / Math.PI
          console.log('Coordonnées RA (heures):', raHours.toFixed(6))
          console.log('Coordonnées DEC (degrés):', decDegrees.toFixed(6))
        }
        
        // Magnitude
        const mag = obj.getInfo('vmag', this.$stel.core.observer)
        console.log('Magnitude (vmag):', mag)
        
        // Distance
        const distance = obj.getInfo('distance', this.$stel.core.observer)
        console.log('Distance:', distance)
        
        // Noms culturels
        try {
          const cultural = obj.culturalDesignations()
          console.log('Noms culturels (culturalDesignations):', cultural)
          
          // Explorer chaque nom culturel en détail
          if (cultural && cultural.length > 0) {
            console.log('--- Détail des noms culturels ---')
            cultural.forEach((name, index) => {
              console.log(`Nom culturel ${index + 1}:`, name)
              if (typeof name === 'object' && name !== null) {
                console.log(`  Détails:`, name)
              }
            })
          }
        } catch (e) {
          console.log('Noms culturels - Erreur:', e.message)
        }
        
        // Données JSON
        try {
          console.log('Données JSON:', JSON.stringify(obj.jsonData))
          
          // Explorer les données JSON pour trouver des noms
          if (obj.jsonData) {
            console.log('--- Exploration des données JSON ---')
            // Variables spécifiques du projet
            const projectVars = ['names', 'culturalNames', 'title', 'givenName', 'catalogueName', 'bayerFlamsteed', 'constellation', 'types', 'hr', 'vmag', 'distanceParsecs', 'distanceLightYears', 'spectralColor']
            
            projectVars.forEach(varName => {
              if (obj.jsonData[varName] !== undefined) {
                console.log(`jsonData.${varName}:`, obj.jsonData[varName])
              }
            })
            
            // Chercher d'autres propriétés qui pourraient contenir des noms
            Object.keys(obj.jsonData).forEach(key => {
              if (key.toLowerCase().includes('name') || key.toLowerCase().includes('title') || key.toLowerCase().includes('bayer') || key.toLowerCase().includes('flamsteed')) {
                if (!projectVars.includes(key)) {
                  console.log(`jsonData.${key}:`, obj.jsonData[key])
                }
              }
            })
          }
        } catch (e) {
          console.log('Données JSON - Erreur:', e.message)
        }
        
        // Type d'objet
        try {
          const otype = obj.getInfo('otype')
          console.log('Type d\'objet:', otype)
        } catch (e) {
          console.log('Type d\'objet - Erreur:', e.message)
        }
        
        // Informations supplémentaires
        try {
          const spectral = obj.getInfo('spectral')
          if (spectral) console.log('Type spectral:', spectral)
        } catch (e) {
          // Ignorer les erreurs
        }
        
        try {
          const parallax = obj.getInfo('parallax')
          if (parallax) console.log('Parallaxe:', parallax)
        } catch (e) {
          // Ignorer les erreurs
        }
        
        console.log('=== FIN OBJET SÉLECTIONNÉ ===')
      } catch (e) {
        console.error('Erreur lors du logging de l\'objet:', e)
      }
    },
    updateCoordinates: function () {
      const obj = this.$stel.core.selection
      if (!obj) return

      // Mise à jour Ra/Dec
      const posCIRS = this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'JNOW', obj.getInfo('radec'))
      const radecCIRS = this.$stel.c2s(posCIRS)
      this.coordinates.ra = this.$stel.anp(radecCIRS[0])
      this.coordinates.dec = this.$stel.anpm(radecCIRS[1])

      // Mise à jour Az/Alt
      const azalt = this.$stel.c2s(this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'OBSERVED', obj.getInfo('radec')))
      this.coordinates.az = this.$stel.anp(azalt[0])
      this.coordinates.alt = this.$stel.anpm(azalt[1])

      // Mise à jour visibilité
      const vis = obj.computeVisibility()
      if (vis.length === 0) {
        this.coordinates.visibility = this.$t('Not visible tonight')
      } else if (vis[0].rise === null) {
        this.coordinates.visibility = this.$t('Always visible tonight')
      } else {
        this.coordinates.visibility = this.$t('Rise: {0}&nbsp;&nbsp;&nbsp; Set: {1}', [
          this.formatTime(vis[0].rise),
          this.formatTime(vis[0].set)
        ])
      }
    },
    formatPhase: function (v) {
      return (v * 100).toFixed(0) + '%'
    },
    formatMagnitude: function (v) {
      if (!v || typeof v !== 'number') {
        return 'Unknown'
      }
      return v.toFixed(2)
    },
    formatDistance: function (d) {
      // d is in AU
      if (!d || typeof d !== 'number') {
        return 'NAN'
      }
      const ly = d * swh.astroConstants.ERFA_AULT / swh.astroConstants.ERFA_DAYSEC / swh.astroConstants.ERFA_DJY
      if (ly >= 0.1) {
        return ly.toFixed(2) + '<span class="radecUnit"> light years</span>'
      }
      if (d >= 0.1) {
        return d.toFixed(2) + '<span class="radecUnit"> AU</span>'
      }
      const meter = d * swh.astroConstants.ERFA_DAU
      if (meter >= 1000) {
        return (meter / 1000).toFixed(2) + '<span class="radecUnit"> km</span>'
      }
      return meter.toFixed(2) + '<span class="radecUnit"> m</span>'
    },
    formatTime: function (jdm) {
      var d = new Date()
      d.setMJD(jdm)
      const utc = new Moment(d)
      utc.utcOffset(this.$store.state.stel.utcoffset)
      return utc.format('HH:mm')
    },
    unselect: function () {
      this.$stel.core.selection = 0
    },
    lockToSelection: function () {
      if (this.$stel.core.selection) {
        this.$stel.pointAndLock(this.$stel.core.selection, 0.5)
      }
    },
    zoomInButtonClicked: function () {
      const currentFov = this.$store.state.stel.fov * 180 / Math.PI
      this.$stel.zoomTo(currentFov * 0.3 * Math.PI / 180, 0.4)
      const that = this
      this.zoomTimeout = setTimeout(_ => {
        that.zoomInButtonClicked()
      }, 300)
    },
    zoomOutButtonClicked: function () {
      const currentFov = this.$store.state.stel.fov * 180 / Math.PI
      this.$stel.zoomTo(currentFov * 3 * Math.PI / 180, 0.6)
      const that = this
      this.zoomTimeout = setTimeout(_ => {
        that.zoomOutButtonClicked()
      }, 200)
    },
    stopZoom: function () {
      if (this.zoomTimeout) {
        clearTimeout(this.zoomTimeout)
        this.zoomTimeout = undefined
      }
    },
    extraButtonClicked: function (btn) {
      btn.callback()
    },
    copyLink: function () {
      const input = document.querySelector('#link_inputid')
      input.focus()
      input.select()
      this.copied = document.execCommand('copy')
      window.getSelection().removeAllRanges()
      this.showShareLinkDialog = false
    },
    formatRA: function (a) {
      const formatInt = function (num, padLen) {
        const pad = new Array(1 + padLen).join('0')
        return (pad + num).slice(-pad.length)
      }
      const raf = this.$stel.a2tf(a, 1)
      return '<div class="radecVal">' + formatInt(raf.hours, 2) + '<span class="radecUnit">h</span>&nbsp;</div><div class="radecVal">' + formatInt(raf.minutes, 2) + '<span class="radecUnit">m</span></div><div class="radecVal">' + formatInt(raf.seconds, 2) + '.' + raf.fraction + '<span class="radecUnit">s</span></div>'
    },
    formatDec: function (a) {
      const formatInt = function (num, padLen) {
        const pad = new Array(1 + padLen).join('0')
        return (pad + num).slice(-pad.length)
      }
      const raf = this.$stel.a2af(a, 1)
      return '<div class="radecVal">' + raf.sign + formatInt(raf.degrees, 2) + '<span class="radecUnit">°</span></div><div class="radecVal">' + formatInt(raf.arcminutes, 2) + '<span class="radecUnit">\'</span></div><div class="radecVal">' + formatInt(raf.arcseconds, 2) + '.' + raf.fraction + '<span class="radecUnit">"</span></div>'
    },
    formatAz: function (a) {
      const formatInt = function (num, padLen) {
        const pad = new Array(1 + padLen).join('0')
        return (pad + num).slice(-pad.length)
      }
      const raf = this.$stel.a2af(a, 1)
      return '<div class="radecVal">' + formatInt(raf.degrees < 0 ? raf.degrees + 180 : raf.degrees, 3) + '<span class="radecUnit">°</span></div><div class="radecVal">' + formatInt(raf.arcminutes, 2) + '<span class="radecUnit">\'</span></div><div class="radecVal">' + formatInt(raf.arcseconds, 2) + '.' + raf.fraction + '<span class="radecUnit">"</span></div>'
    },
    initWaveSurfer() {
      // Vérifie que le composant n'est pas détruit et que les refs existent
      if (!this.$refs.wavesurfer || !this.$refs.wavesurferSpectro || !this.selectedObject || !this.selectedObject.hr) {
        setTimeout(() => {
          if (this._isBeingDestroyed || this._isDestroyed) return;
          this.initWaveSurfer();
        }, 30)
        return
      }
      // Détruit l'ancienne instance si besoin
      if (this.wavesurfer) {
        this.wavesurfer.destroy();
        this.wavesurfer = null;
      }
      const audioBaseUrl = process.env.VUE_APP_DOMAIN_NAME.includes('localhost')
        ? '/stellar-sound'
        : 'https://cdn.stellardiamonds.org/uploads/stellar/sound'
      const audioUrl = `${audioBaseUrl}/${this.selectedObject.hr}.mp3`
      try {
        this.wavesurfer = WaveSurfer.create({
          container: this.$refs.wavesurferSpectro,
          barHeight: 5,
          barWidth: 9,
          width: 320,
          height: 130,
          normalize: true,
          waveColor: "#9e9e9e",
          progressColor: "#425b97",
          cursorColor: "#ddd5e9",
          cursorWidth: 0,
          barGap: 10,
          barRadius: 12,
          barAlign: "",
          minPxPerSec: 100,
          fillParent: true,
          autoScroll: true,
          dragToSeek: true,
          autoCenter: true,
          url: audioUrl,
        })
        this.wavesurfer.setVolume(0.7)
        this.wavesurfer.on('play', () => { this.isPlaying = true })
        this.wavesurfer.on('pause', () => { this.isPlaying = false })
        this.wavesurfer.on('finish', () => { this.isPlaying = false })
        this.wavesurfer.on('ready', () => {
          this.audioExists = true;
          this.wavesurfer.isReady = true;
          if (this.isPlaying) {
            this.wavesurfer.play()
          }
        });
      } catch (e) {
        console.error('Erreur lors de l\'init de WaveSurfer:', e);
        this.audioExists = false;
      }
    },
    playStarSound() {
      if (this.wavesurfer) {
        if (this.isPlaying) {
          this.wavesurfer.pause()
        } else {
          this.wavesurfer.play()
        }
      }
    },
    formatEuroValue(val) {
      if (val === undefined || val === null || val === '') return ''
      let num = Number(val)
      if (isNaN(num)) return val
      // Arrondi à 2 décimales
      num = Math.round(num * 100) / 100
      // Format avec séparateur de milliers
      const parts = num.toFixed(2).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      // Supprime les décimales si égal à 00
      const formatted = parts[1] === '00' ? parts[0] : parts.join(',')
      return formatted + ' €'
    },
    getDiamondStyle(color) {
      color = color ? color.toLowerCase() : ''
      switch (color) {
        case 'blanc':
          return { background: 'rgba(245,245,245,0.9)', color: '#222', darkText: true }
        case 'jaunâtre':
          return { background: 'rgba(255,245,180,0.95)', color: '#222', darkText: true }
        case 'jaune':
          return { background: 'rgba(255,224,130,0.9)', color: '#222', darkText: true }
        case 'bleu':
          return { background: 'rgba(70,99,172,0.8)', color: '#fff', darkText: false }
        case 'orange':
          return { background: 'rgba(255,210,120,0.95)', color: '#222', darkText: true }
        case 'rouge':
          return { background: 'rgba(229,115,115,0.9)', color: '#fff', darkText: false }
        default:
          return { background: 'rgba(66, 66, 66, 0.6)', color: '#fff', darkText: false }
      }
    }
  },
  mounted: function () {
    const that = this
    window.addEventListener('mouseup', function (event) {
      that.stopZoom()
    })
  },
  beforeDestroy: function () {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = undefined
    }
    if (this.wavesurfer) {
      this.wavesurfer.destroy()
      this.wavesurfer = null
    }
  }
}
</script>

<style>
/* Import des Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@300&family=Open+Sans:wght@400&display=swap');

.image-container {
  margin: 0 0 20px 0;
  text-align: center;
  width: 100%;
}

.diamond-link {
  display: block;
  width: 100%;
  max-width: 100%;
  transition: transform 0.2s ease;
}

.object-video {
  width: 100%;
  height: auto;
  margin: 0;
  border-radius: 8px;
  max-width: 100%;
  display: block;
}

.bt_disabled {
  filter: opacity(0.2);
}

.radecVal {
  display: inline-block;
  font-family: monospace;
  padding-right: 2px;
  font-size: 13px;
  font-weight: bold;
}

.radecUnit {
  color: #dddddd;
  font-weight: normal
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.info-section {
  padding: 15px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  margin-bottom: 20px;
}

/* Fond transparent pour le panneau d'expansion */
.expansion-panels {
  background: transparent !important;
}

.v-expansion-panel {
  background: rgba(0, 0, 0, 0.2) !important;
  color: #ffffff !important;
  margin-bottom: 2px !important;
}

.v-expansion-panel::before {
  box-shadow: none !important;
}

.panel-header {
  color: #dddddd !important;
  font-size: 14px !important;
}

.v-expansion-panel-content__wrap {
  padding: 0px !important;
}

.diamond-grid {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  margin: 0 !important;
  padding: 4px !important;
}

.diamond-grid-item {
  padding: 2px;
  text-align: center;
}

/* Style spécial pour le type de diamant stellaire */
.v-col-12 .diamond-grid-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2px;
  padding-bottom: 4px;
}

.waveform-container {
  margin-top: 0 !important;
  border-radius: 0 !important;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}



.info-section.mb-4+.spectrogram-below-panel {
  margin-top: 0 !important;
}

.info-row {
  padding: 8px 0;
  font-size: 14px;
}

.text-h6 {
  font-family: 'Source Serif 4', serif !important;
  font-weight: 300;
  font-size: 1.25rem;
  line-height: 2rem;
  letter-spacing: 0.0125em;
  color: #D7D7D7;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 15px;
  text-transform: uppercase;
}

.v-card-text {
  padding: 20px !important;
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px !important;
  /* Pour Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.6) transparent;
}

/* Pour Chrome, Safari et autres */
.scrollable-content::-webkit-scrollbar {
  width: 8px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: transparent;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 4px;
}

/* Ajustement pour que le contenu ne soit pas trop serré */
.info-section:last-child {
  margin-bottom: 0;
}

.other-names {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
}

.other-names-label {
  color: #dddddd;
  margin-right: 16px;
}

.grid-label {
  color: white;
  font-weight: 500;
  font-size: 16px;
}

.grid-value {
  color: white;
  font-weight: 500;
  font-size: 16px;
}

/* Mise à jour du texte général */
.info-row,
.grid-label,
.grid-value,
.other-names,
.panel-header,
.text-body-2 {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
}

.text-h5 {
  font-family: 'Source Serif 4', serif;
  font-weight: 300;
}

.field-description {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 0.9rem;
  color: #dddddd;
  letter-spacing: 0.02em;
  opacity: 0.85;
  padding-bottom: 4px;
  font-style: italic;
}

/* Mise à jour des autres styles utilisant la même présentation */
.grid-label {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 0.9rem;
  color: #dddddd;
  letter-spacing: 0.02em;
  opacity: 0.85;
  margin-bottom: 2px;
  font-style: italic;
}

/* Pour maintenir la cohérence dans les panneaux d'expansion */
.v-expansion-panel .field-description {
  padding-bottom: 2px;
}

/* Correction du style des titres */
.v-application .text-h6,
.text-h6 {
  font-family: 'Source Serif 4', serif !important;
  font-weight: 300 !important;
  font-size: 1.25rem !important;
  line-height: 2rem !important;
  letter-spacing: 0.0125em !important;
  color: #D7D7D7 !important;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 15px;
  text-transform: uppercase;
}

/* Même chose pour les titres h5 */
.v-application .text-h5,
.text-h5 {
  font-family: 'Source Serif 4', serif !important;
  font-weight: 300 !important;
}

/* Ajout de la classe pour le fond bleu foncé */
.info-section.has-diamond {
  background: rgba(20, 66, 102, 0.8);
  /* #144266 avec opacité */
}

.diamond-background {
  padding: 0px;
  border-radius: 12px;
  width: 100%;
  margin-bottom: 20px;
}

.object-video {
  width: 100%;
  height: auto;
  margin: 0;
  border-radius: 8px;
  max-width: 100%;
  display: block;
}

.diamond-cta {
  background: linear-gradient(45deg, #4663ac 30%, #5a7ac7 90%) !important;
  color: white !important;
  padding: 0 25px !important;
  height: 54px !important;
  font-size: 1.1rem !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  border-radius: 27px !important;
  box-shadow: 0 3px 15px rgba(70, 99, 172, 0.4) !important;
  transition: all 0.3s ease !important;
  display: flex;
  align-items: center;
}

.diamond-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(70, 99, 172, 0.6) !important;
}

.diamond-cta .v-icon {
  margin-right: 10px;
  font-size: 24px !important;
}

.availability-text {
  font-size: 1rem;
  font-weight: 500;
  opacity: 0.9;
}

.availability-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  height: 100%;
}

.availability-text {
  font-size: 1rem;
  font-weight: 500;
  opacity: 0.9;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.v-icon.v-icon {
  display: inline-flex;
  align-items: center;
  height: 24px;
  width: 24px;
  line-height: 1;
}

.diamond-dark-text,
.diamond-dark-text * {
  color: #111 !important;
}

.diamond-dark-text .white--text,
.diamond-dark-text .grey--text,
.diamond-dark-text .text-caption,
.diamond-dark-text .text-body-2,
.diamond-dark-text .subtitle-1,
.diamond-dark-text .caption,
.diamond-dark-text a {
  color: #111 !important;
  opacity: 0.95 !important;
}

.diamond-dark-text .text-h6,
.diamond-dark-text .v-application .text-h6 {
  color: #111 !important;
  opacity: 0.98 !important;
}

.spectro-panel-bg .v-expansion-panel {
  background: rgba(66, 66, 66, 0.6) !important;
  color: #fff !important;
}
</style>
